import React, { useState, useEffect, useRef } from 'react';
import CFAdminDataTable from '../../components/CFAdminDataTable';
import { MissingRule } from './index';
import VinImportService from "../../services/VinImportService";
import CFAdminNotification from "../../components/CFAdminNotification";

const MissingRuleCell = ({ row }: { row: any }) => (
    <div>
        If {row.original.attributeName.replace(/^s/, 'v')} = '{row.original.attributeValue}' then
    </div>
);

const SelectInputCell = ({ row, column }: { row: any; column: any }) => (
    <div>
        <div data-testid="select-input-container">
            {row.original.attributeName} =
        </div>
        <input
            type="radio"
            name={`radio-${row.index}`}
            checked={row.original.selectedInput === 'select'}
            onChange={() => column.handleRadioChange(row.index, 'select')}
        />
        <select
            value={row.original.selectedValue || ''}
            onChange={(e) => {
                const selectedValue = e.target.value;
                const selectedDescription = e.target.options[e.target.selectedIndex].text;
                column.onSelectChange(row.index, selectedValue, selectedDescription);
            }}
            disabled={row.original.selectedInput !== 'select'}
        >
            {(column.attributeSelectValues[row.original.attributeId] || []).map((value: any) => (
                <option key={value.attributeValueId} value={value.attributeValueId}>{value.attributeValueDescription}</option>
            ))}
        </select>
        <br/>
        <input
            type="radio"
            name={`radio-${row.index}`}
            checked={row.original.selectedInput === 'text'}
            onChange={() => column.handleRadioChange(row.index, 'text')}
        />
        <input
            type="text"
            defaultValue={row.original.enteredValue || ''}
            ref={(el) => (column.inputRefs.current[row.index] = el)}
            onBlur={() => column.handleBlur(row.index)}
            disabled={row.original.selectedInput !== 'text'}
        />
    </div>
);

const FordFlagCell = ({ row, column }: { row: any; column: any }) => (
    <div>
        <input
            type="checkbox"
            checked={row.original.fordFlag || false}
            onChange={(e) => {
                column.onFordFlagChange(row.index, e.target.checked);
            }}
        />
    </div>
);

const CFAdminMissingRules = ({ inputData, setInputData, isSaved, setIsSaved, isApplyRules}: { inputData: MissingRule[], setInputData: React.Dispatch<React.SetStateAction<MissingRule[]>>, isSaved: boolean, setIsSaved: React.Dispatch<React.SetStateAction<boolean>>, isApplyRules: React.Dispatch<React.SetStateAction<boolean>> }) => {
    const childCompRef = useRef<any>();
    const [canSave, setCanSave] = useState(false);
    const [attributeIds, setAttributeIds] = useState<number[]>([]);
    const [attributeSelectValues, setAttributeSelectValues] = useState<{ [key: number]: { attributeValueId: number, attributeValueDescription: string }[] }>({});
    const inputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

    const handleRadioChange = (rowIndex: number, selectedInput: 'select' | 'text') => {
        const newData: MissingRule[] = [...inputData];
        newData[rowIndex] = { ...newData[rowIndex], selectedInput };
        setInputData(newData);
    };

    const handleBlur = (rowIndex: number) => {
        const newData: MissingRule[] = [...inputData];
        newData[rowIndex] = { ...newData[rowIndex], enteredValue: inputRefs.current[rowIndex]?.value || '' };
        setInputData(newData);
    };

    function arraysEqual(arr1: number[], arr2: number[]): boolean {
        if (arr1.length !== arr2.length) {
            return false;
        }
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }
        return true;
    }

    useEffect(() => {
        const allRowsFilled = inputData.length > 0 && inputData.every(row => row.selectedValue || row.enteredValue);
        setCanSave(allRowsFilled);
        if (inputData.length === 0) {
            setIsSaved(true);
        }
        let data: number[] = Array.from(new Set(inputData.map(row => row.attributeId).sort((a, b) => a - b)));

        if (!arraysEqual(data, attributeIds)) {
            setAttributeIds(() => {
                return [];
            });
            setAttributeIds(() => {
                return data;
            })
        }
    }, [inputData, attributeIds]);

    useEffect(() => {
        if (attributeIds.length === 0) return;
        retrieveAttributeDetails(attributeIds).then(r => {});
    }, [attributeIds]);


    const retrieveAttributeDetails = async (data: number[]) => {
        data.forEach(attributeId => {
            let requestBody = {
                attributeIds: [attributeId]
            }
            VinImportService.getVehicleAttributeValues(requestBody)
                .then(response => {
                    processVehicleAttributeValues(response, attributeId);
                })
                .catch(e => {
                    console.log(e);
                    confirmErrorMessage("Error retrieving attribute values");
                });
        });
    };
    const processVehicleAttributeValues = (response: any, attributeId: any) => {
        response.data.attributeValues
            .sort((a: { attributeValueId: number, attributeValueDescription: string }, b: {
                attributeValueId: number,
                attributeValueDescription: string
            }) => a.attributeValueDescription.localeCompare(b.attributeValueDescription))
            .forEach((attributeValue: { attributeValueId: number, attributeValueDescription: string }) => {
                handleAttributeSelectValues(attributeId, attributeValue.attributeValueId, attributeValue.attributeValueDescription);
            });
    };

    const confirmSuccessMessage = async (value: any) => {
        childCompRef.current.successMessage(value);
    };

    const confirmErrorMessage = async (value: any) => {
        childCompRef.current.failureMessage(value);
    };

    const handleSave = async () => {
        setIsSaved(true);
        setCanSave(false);
        console.log(JSON.stringify(inputData));
        try {
            const savePromises = inputData.map(rule => {
                let requestBody: any = {
                    sourceAttributeValues: [
                        {
                            sourceColumn: rule.attributeName.replace(/^s/, '').replace(/^./, str => str.toLowerCase()),
                            sourceValue: rule.attributeValue
                        }
                    ],
                    destinationAttributeValues: [
                        {
                            attributeId: rule.attributeId,
                            attributeValueId: rule.selectedInput === 'select' && rule.selectedValue ? +rule.selectedValue : null,
                            attributeValueDescription: rule.selectedInput === 'text' ? rule.enteredValue : rule.selectedDescription
                        }
                    ],
                    fordFlag: rule.fordFlag
                }
                return VinImportService.saveRule(requestBody);
            });
            await Promise.all(savePromises);
            confirmSuccessMessage("All rules saved successfully");
            setIsSaved(true);
            isApplyRules(true);
        } catch (error) {
            confirmErrorMessage("Error saving one or more rules");
        }
        setInputData(inputData);
    };

    const handleAttributeSelectValues = (attributeId: number, attributeValueId: number, attributeValueDescription: string) => {
        setAttributeSelectValues(prevState => ({
            ...prevState,
            [attributeId]: [
                ...(prevState[attributeId] || []).filter(value => value.attributeValueId !== attributeValueId),
                { attributeValueId, attributeValueDescription }
            ]
        }));
    }

    const columns = [
        {
            Header: "Missing Rule",
            accessor: 'attributeId',
            Cell: MissingRuleCell
        },
        {
            Header: 'Select existing Value or Enter new Value',
            accessor: 'selectInput',
            Cell: SelectInputCell,
            handleRadioChange: handleRadioChange,
            handleBlur: handleBlur,
            inputRefs: inputRefs,
            attributeSelectValues: attributeSelectValues,
            onSelectChange: (rowIndex: number, selectedValue: string, selectedDescription: string) => {
                const newData: MissingRule[] = [...inputData];
                newData[rowIndex] = { ...newData[rowIndex], selectedValue, selectedDescription };
                setInputData(newData);
            }
        },
        {
            Header: 'Ford Flag',
            accessor: 'fordFlag',
            Cell: FordFlagCell,
            onFordFlagChange: (rowIndex: number, fordFlag: boolean) => {
                const newData: MissingRule[] = [...inputData];
                newData[rowIndex] = { ...newData[rowIndex], fordFlag };
                setInputData(newData);
            }
        }
    ];

    return (
        <div>
            <CFAdminDataTable
                columns={columns}
                data={inputData}
                showFilter={false}
                showAdd={false}
                showPagination={false}
                pageSize={1000}
            />
            <button
                className="fvp-button button-right-align"
                type="button"
                onClick={handleSave} disabled={!canSave}>Save

            </button>
            <CFAdminNotification ref={childCompRef}/>
        </div>
    );
};

export default CFAdminMissingRules;